import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import { PRO_TIER_NAME } from '../lib/productTier';

const CONTACT_EMAIL = 'support.peakperformanceiq@gmail.com';
const LAST_UPDATED = 'August 6, 2026';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-base font-semibold text-zinc-100">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-zinc-400">{children}</div>
    </section>
  );
}

export function TermsPage() {
  return (
    <div className="app-body min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <PageHeader
          title="Terms of Use"
          subtitle={`Peak Performance IQ · Last updated ${LAST_UPDATED}`}
          compact={false}
        />

        <div className="glass-panel rounded-2xl p-5 sm:rounded-3xl sm:p-8">
          <p className="mb-8 text-sm leading-relaxed text-zinc-400">
            These Terms of Use (&quot;Terms&quot;) govern your use of Peak Performance IQ, including the
            website at{' '}
            <a
              href="https://peakperformanceiq.com"
              className="text-sky-400 underline-offset-2 hover:underline"
            >
              peakperformanceiq.com
            </a>
            , the iOS app, and related services (the &quot;Service&quot;). By creating an account or
            using the Service, you agree to these Terms.
          </p>

          <Section title="The Service">
            <p>
              Peak Performance IQ provides fitness, nutrition, sleep, and coaching tools for personal
              use. Features may include logging meals and workouts, progress tracking, community
              Circles, and AI-assisted coaching ({PRO_TIER_NAME}).
            </p>
            <p>
              The Service is for informational and educational purposes only. It is{' '}
              <strong className="font-medium text-zinc-300">not medical, nutritional, or
              professional advice</strong>. Always consult a qualified professional before changing
              your diet, training, or health routines.
            </p>
          </Section>

          <Section title="Accounts">
            <p>
              You must provide accurate information and keep your login credentials secure. You are
              responsible for activity under your account. We may suspend or terminate accounts that
              violate these Terms or misuse the Service.
            </p>
          </Section>

          <Section title="Subscriptions &amp; payments">
            <p>
              Some features require a paid subscription (for example, {PRO_TIER_NAME} or an ad-free
              plan). Prices, billing periods, and free trials (if any) are shown at purchase.
            </p>
            <p>
              <strong className="font-medium text-zinc-300">iOS App Store purchases:</strong>{' '}
              Subscriptions bought through Apple are billed to your Apple ID and managed by Apple.
              Payment is charged when you confirm purchase (or when a free trial ends, if offered).
              Subscriptions renew automatically unless you cancel at least 24 hours before the end of
              the current period. You can manage or cancel in iOS Settings → Apple ID → Subscriptions,
              or via Manage subscription in the app.
            </p>
            <p>
              <strong className="font-medium text-zinc-300">Web purchases:</strong> Subscriptions bought
              on the website are processed by Stripe under Stripe&apos;s terms. Sign in at{' '}
              <a href="https://peakperformanceiq.com/account" className="text-zinc-300 underline">
                peakperformanceiq.com/account
              </a>{' '}
              to cancel, update your payment method, or view invoices.
            </p>
            <p>
              Refunds for App Store purchases are handled by Apple under Apple&apos;s refund policies.
              Web refunds are handled according to applicable law and our support process.
            </p>
          </Section>

          <Section title="AI features">
            <p>
              {PRO_TIER_NAME} and related AI tools may send relevant profile and logged data to our
              service providers to generate responses. Outputs can be inaccurate or incomplete. Do
              not rely on them for medical decisions. Do not submit sensitive information you are
              unwilling to share with those systems.
            </p>
          </Section>

          <Section title="Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Misuse the Service, attempt unauthorized access, or disrupt our systems</li>
              <li>Scrape, reverse engineer, or resell the Service without permission</li>
              <li>Upload unlawful, abusive, or infringing content</li>
              <li>Use the Service in any way that violates applicable law</li>
            </ul>
          </Section>

          <Section title="Intellectual property">
            <p>
              The Service, branding, and content we provide are owned by Peak Performance IQ or our
              licensors. You retain rights to content you submit, and grant us a limited license to
              host and process it to operate the Service.
            </p>
          </Section>

          <Section title="Disclaimer &amp; limitation of liability">
            <p>
              The Service is provided &quot;as is&quot; without warranties of any kind, to the fullest
              extent permitted by law. To the extent permitted by law, Peak Performance IQ is not
              liable for indirect, incidental, special, consequential, or punitive damages, or for
              loss of data, profits, or health outcomes arising from your use of the Service.
            </p>
          </Section>

          <Section title="Privacy">
            <p>
              Our{' '}
              <Link to="/privacy" className="text-sky-400 underline-offset-2 hover:underline">
                Privacy Policy
              </Link>{' '}
              explains how we collect and use personal information. By using the Service, you also
              acknowledge that policy.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update these Terms from time to time. We will post the revised version on this
              page and update the &quot;Last updated&quot; date. Continued use after changes means
              you accept the updated Terms.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about these Terms? Email{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sky-400 underline-offset-2 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </div>

        <p className="mt-8 text-center text-sm text-zinc-500">
          <Link to="/privacy" className="text-zinc-400 underline-offset-2 hover:text-zinc-300 hover:underline">
            Privacy Policy
          </Link>
          <span className="mx-2 text-zinc-600">·</span>
          <Link to="/login" className="text-zinc-400 underline-offset-2 hover:text-zinc-300 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
